import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import strings from "translate/language";
import { getCpSmList } from "reduxStore/action/channelPartnerAction";
import { useDispatch, useSelector } from "react-redux";
import TableDataNotFound from "component/TableDataNotFound";
import { handleListResponse, seo } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import Pagination from "component/common/Pagination";
import propertyService from "reduxStore/service/propertyService";
import apiEndPoints from "utils/apiEndPoints";
import { apiCall, errorToast, successToast } from "utils/httpClient";
import moment from "moment";
import "./ChannelPartnerListing.css";

const PAGE_SIZE = 20;
const VISIBLE_PROPERTY_CHIPS = 6;

const listCpProperties = (item) => {
  const rows = Array.isArray(item?.properties) ? item.properties : [];
  const cleaned = rows
    .map((row) => ({
      property_id: String(row?.property_id?._id || row?.property_id || ""),
      property_title: row?.property_title || "",
    }))
    .filter((row) => row.property_id || row.property_title);
  if (cleaned.length) {
    return cleaned;
  }
  const fallbackId = String(item?.property_id?._id || item?.property_id || "");
  if (!fallbackId && !item?.property_title) {
    return [];
  }
  return [
    {
      property_id: fallbackId,
      property_title: item?.property_title || "",
    },
  ];
};

const EMPTY_FILTERS = {
  search_by_name: "",
  property_name: "",
  location: "",
  start_date: "",
  end_date: "",
  status: "",
};

const CpSmList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [getchannelPartnerList, setchannelPartnerList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sourcingManagerList, setSourcingManagerList] = useState([]);
  const [selectedSourcingManager, setSelectedSourcingManager] = useState("");
  const [isManagerSelection, setManagerSelection] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [expandedPropertyRows, setExpandedPropertyRows] = useState({});

  const channelPartnerListSelector = useSelector((state) => state?.cpSmData);
  const permissionSelector = useSelector((state) => state.permission);
  const { isAdmin = false } = permissionSelector;

  const { view, edit } = usePermission({
    create: "add_channelpartner",
    view: "view_channelpartner",
    edit: "edit_channelpartner",
  });

  const fetchList = (offset = page, nextFilters = appliedFilters) => {
    dispatch(
      getCpSmList({
        limit: PAGE_SIZE,
        offset,
        search_by_name: nextFilters.search_by_name,
        property_name: nextFilters.property_name,
        location: nextFilters.location,
        start_date: nextFilters.start_date,
        end_date: nextFilters.end_date,
        status: nextFilters.status,
      })
    );
  };

  useEffect(() => {
    fetchList(0, appliedFilters);
    setPage(0);
    propertyService
      .getAllProperty({
        offset: 0,
        limit: 300,
        skip_metrics: true,
        suppressGlobalLoader: true,
      })
      .then((res) => {
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        const options = rows
          .map((row) => ({
            value: row.property_title || row._id,
            label: row.property_title || row.property_name,
            createdDate: row.createdDate,
          }))
          .filter((row) => row.label)
          .sort(
            (a, b) =>
              new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
          );
        setPropertyOptions(options);
      })
      .catch(() => setPropertyOptions([]));
  }, [dispatch]);

  useEffect(() => {
    const { response = null } = channelPartnerListSelector || {};
    if (response && response?.status === 200) {
      setchannelPartnerList(
        handleListResponse(response?.data) ? response.data : []
      );
      setTotalCount(Number(response.total_data || response.data?.length || 0));
    } else {
      setchannelPartnerList([]);
      setTotalCount(0);
    }
  }, [channelPartnerListSelector]);

  useEffect(() => {
    seo({
      title: "Allocate CP to SM",
      metaDescription: "Assign channel partners to sourcing managers",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(0);
    fetchList(0, filters);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
    fetchList(0, EMPTY_FILTERS);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    fetchList(nextPage, appliedFilters);
  };


  const smSelectOptions = useMemo(() => {
    return sourcingManagerList.map((manager) => {
      const roleText = String(
        manager.role_slug || manager.role_title || ""
      ).toUpperCase();
      const roleTag =
        roleText.includes("SCM") || roleText.includes("CLUSTER")
          ? "SCM"
          : "SM";
      return {
        value: manager._id,
        label: manager.user_name,
        role: roleTag,
      };
    });
  }, [sourcingManagerList]);

  const loadPropertySourcingManagers = async (propertyId) => {
    setSourcingManagerList([]);
    if (!propertyId || propertyId === "-") {
      return;
    }
    try {
      const propertySms = await apiCall(
        "post",
        apiEndPoints.GET_PROPERTY_BASED_SM,
        { property_id: propertyId }
      );
      const rows = Array.isArray(propertySms?.data?.data)
        ? propertySms.data.data
        : [];
      setSourcingManagerList(rows);
    } catch (error) {
      setSourcingManagerList([]);
    }
  };

  const handleApprove = async (item) => {
    if (!selectedSourcingManager) {
      errorToast("Please select a sourcing manager.");
      return;
    }
    const params = {
      cp_id: [item._id],
      user_id: selectedSourcingManager,
      assign_type: selectedPropertyId ? 2 : 1,
    };
    if (selectedPropertyId) {
      params.property_id = selectedPropertyId;
    }
    try {
      const response = await apiCall(
        "post",
        apiEndPoints.CPASSIGNSOURCINGMANAGER,
        params
      );
      if (response?.data?.status === 200) {
        successToast(response?.data?.message);
        fetchList(page, appliedFilters);
      } else {
        errorToast(response?.data?.message);
      }
    } catch (error) {
      errorToast(error);
    }
    setSelectedItem(null);
    setSelectedSourcingManager("");
    setSelectedPropertyId("");
    setManagerSelection(false);
    setModalOpen(false);
  };

  const openApprovalModal = async (item) => {
    const properties = listCpProperties(item);
    const propertyId = String(
      properties[0]?.property_id || item?.property_id?._id || item?.property_id || ""
    ).trim();
    setSelectedItem(item);
    setSelectedSourcingManager("");
    setSelectedPropertyId(propertyId);
    setManagerSelection(true);
    setModalOpen(true);
    await loadPropertySourcingManagers(propertyId);
  };

  const handlePropertyPick = async (propertyId) => {
    setSelectedPropertyId(propertyId);
    setSelectedSourcingManager("");
    await loadPropertySourcingManagers(propertyId);
  };

  const closeApproveModal = () => {
    setSelectedItem(null);
    setSelectedSourcingManager("");
    setSelectedPropertyId("");
    setManagerSelection(false);
    setModalOpen(false);
  };

  return (
    <div className="page-body justo-cp-page">
      <Breadcrumb
        title={strings.allocateCpToSm}
        parentListing={strings.channelPartner}
        url="/channelpartner"
        titleactive={strings.allocateCpToSm}
        isBackBtn={true}
      />
      <div className="container-fluid justo-cp">
        <div className="justo-cp__card">
          <div className="justo-cp__top">
            <div>
              <h1 className="justo-cp__title">{strings.listOfChannelPartner}</h1>
              <p className="justo-cp__subtitle">
                To assign a CP: click Allocate on a row, pick the sourcing
                manager, then Confirm. Newest CPs are on page 1.
              </p>
            </div>
          </div>

          <form
            className="justo-cp__filters"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="justo-cp__filters-grid justo-cp__filters-grid--4">
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.searchByName}</span>
                <input
                  className="form-control justo-cp__input"
                  type="text"
                  name="search_by_name"
                  value={filters.search_by_name}
                  onChange={handleFilterChange}
                  placeholder={strings.agencyName}
                />
              </label>
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.propertyName}</span>
                <Select
                  classNamePrefix="justo-cp-select"
                  isClearable
                  isSearchable
                  options={propertyOptions}
                  value={
                    propertyOptions.find(
                      (option) => option.value === filters.property_name
                    ) || null
                  }
                  placeholder="Select property"
                  onChange={(option) =>
                    setFilters((prev) => ({
                      ...prev,
                      property_name: option?.value || "",
                    }))
                  }
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 20 }),
                  }}
                />
              </label>
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.status}</span>
                <select
                  className="form-control justo-cp__input"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  <option value="active">{strings.active}</option>
                  <option value="inactive">{strings.inactive}</option>
                </select>
              </label>
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.location}</span>
                <input
                  className="form-control justo-cp__input"
                  type="text"
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                />
              </label>
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.startDate}</span>
                <input
                  className="form-control justo-cp__input"
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                />
              </label>
              <label className="justo-cp__field">
                <span className="justo-cp__label">{strings.endDate}</span>
                <input
                  className="form-control justo-cp__input"
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  min={filters.start_date}
                  onChange={handleFilterChange}
                />
              </label>
              <div className="justo-cp__filter-actions">
                <button
                  type="submit"
                  className="justo-cp__btn justo-cp__btn--solid"
                >
                  {strings.search}
                </button>
                <button
                  type="button"
                  className="justo-cp__btn justo-cp__btn--ghost"
                  onClick={handleReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          <div className="justo-cp__scroll">
            <table className="table table-border-horizontal justo-cp-sm-table">
              <thead>
                <tr>
                  <th className="col-agency">{strings.agencyName}</th>
                  <th className="col-property">{strings.propertyName}</th>
                  <th className="col-created">{strings.createdDate}</th>
                  <th className="col-location">{strings.location}</th>
                  <th className="col-rera">{strings.reraNo}</th>
                  <th className="col-status">{strings.status}</th>
                  <th className="col-action">{strings.action}</th>
                </tr>
              </thead>
              <tbody>
                {getchannelPartnerList?.length > 0 ? (
                  getchannelPartnerList.map((item) => (
                    <tr key={item._id}>
                      <td className="col-agency">
                        {item?.agency_name || item?.agent_name || "-"}
                      </td>
                      <td className="col-property">
                        {(() => {
                          const properties = listCpProperties(item);
                          if (!properties.length) {
                            return "-";
                          }
                          const isExpanded =
                            expandedPropertyRows[item._id] === true;
                          const visible = isExpanded
                            ? properties
                            : properties.slice(0, VISIBLE_PROPERTY_CHIPS);
                          return (
                            <div className="justo-cp-property-chips">
                              {visible.map((property) => (
                                <span
                                  key={
                                    property.property_id ||
                                    property.property_title
                                  }
                                  className="justo-cp-property-chip"
                                  title={property.property_title}
                                >
                                  {property.property_title || "-"}
                                </span>
                              ))}
                              {properties.length > VISIBLE_PROPERTY_CHIPS ? (
                                <button
                                  type="button"
                                  className="justo-cp-property-chip justo-cp-property-chip--more"
                                  onClick={() =>
                                    setExpandedPropertyRows((prev) => ({
                                      ...prev,
                                      [item._id]: !isExpanded,
                                    }))
                                  }
                                >
                                  {isExpanded
                                    ? "Show less"
                                    : `+${
                                        properties.length - VISIBLE_PROPERTY_CHIPS
                                      } more`}
                                </button>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="col-created">
                        {item.createdDate
                          ? moment(item.createdDate).format("DD-MM-YYYY hh:mm a")
                          : "-"}
                      </td>
                      <td className="col-location">{item.location || "-"}</td>
                      <td className="col-rera">
                        {item.rera_certificate_no || "-"}
                      </td>
                      <td className="col-status">
                        <span
                          className={`justo-status-pill ${
                            item.active_status
                              ? "justo-status-pill--active"
                              : "justo-status-pill--inactive"
                          }`}
                        >
                          <i
                            className={`fa ${
                              item.active_status
                                ? "fa-check-circle"
                                : "fa-exclamation-circle"
                            }`}
                            aria-hidden="true"
                          />
                          <span>
                            {item.active_status
                              ? strings.active
                              : strings.inactive}
                          </span>
                        </span>
                      </td>
                      <td className="col-action">
                        <div className="cp-listing__actions">
                          {view ? (
                            <button
                              type="button"
                              className="cp-listing__action-btn"
                              title={strings.viewChannelPartner}
                              onClick={() =>
                                navigate(
                                  `/viewchannelpartner/${
                                    item.cp_id || item._id
                                  }`
                                )
                              }
                            >
                              <i className="fa fa-eye" aria-hidden="true" />
                            </button>
                          ) : null}
                          {edit && !isAdmin ? (
                            <button
                              type="button"
                              className="cp-listing__secondary-btn cp-listing__secondary-btn--success"
                              onClick={() => openApprovalModal(item)}
                            >
                              {strings.allocate}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr />
                )}
              </tbody>
            </table>
            <TableDataNotFound data={getchannelPartnerList} />
          </div>

          <Pagination
            itemsPerPage={PAGE_SIZE}
            totalCount={totalCount}
            listData={getchannelPartnerList}
            onPageChange={handlePageChange}
            itemOffset={page}
            setItemOffset={setPage}
          />
        </div>
      </div>

      {isModalOpen ? (
        <div className="justo-cp-sm-modal__backdrop" role="presentation">
          <div className="justo-cp-sm-modal" role="dialog" aria-modal="true">
            <div className="justo-cp-sm-modal__head">
              <div>
                <h5 className="justo-cp-sm-modal__title">
                  Assign sourcing manager
                </h5>
                <p className="justo-cp-sm-modal__meta">
                  {selectedItem?.agency_name || selectedItem?.agent_name}
                </p>
              </div>
              <button
                type="button"
                className="justo-cp-sm-modal__close"
                onClick={closeApproveModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="justo-cp-sm-modal__body">
              <label className="justo-cp-sm-modal__label">Property</label>
              {listCpProperties(selectedItem).length ? (
                <div className="justo-cp-property-chips justo-cp-property-chips--modal">
                  {listCpProperties(selectedItem).map((property) => (
                    <button
                      type="button"
                      key={property.property_id || property.property_title}
                      className={`justo-cp-property-chip ${
                        selectedPropertyId === property.property_id
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() => handlePropertyPick(property.property_id)}
                    >
                      {property.property_title || "-"}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="justo-cp-sm-modal__hint">
                  This channel partner has no property assigned.
                </p>
              )}
              <label className="justo-cp-sm-modal__label">
                Sourcing manager
              </label>
              <Select
                classNamePrefix="justo-cp-select"
                isClearable
                isSearchable
                placeholder="Select SM or SCM"
                options={smSelectOptions}
                value={
                  smSelectOptions.find(
                    (option) => option.value === selectedSourcingManager
                  ) || null
                }
                formatOptionLabel={(option) => (
                  <span className="justo-cp-sm-modal__option">
                    <span>{option.label}</span>
                    <span className="justo-cp-sm-modal__role">{option.role}</span>
                  </span>
                )}
                onChange={(option) =>
                  setSelectedSourcingManager(option?.value || "")
                }
                noOptionsMessage={() =>
                  selectedPropertyId
                    ? "No SM or SCM is allocated to this property in JW."
                    : "This channel partner has no property assigned."
                }
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 10060 }),
                }}
              />
              <p className="justo-cp-sm-modal__hint">
                {selectedPropertyId
                  ? sourcingManagerList.length
                    ? "Showing SM and SCM allocated to this property in JW."
                    : "No SM or SCM is allocated to this property in JW."
                  : "This channel partner has no property assigned."}
              </p>
            </div>
            <div className="justo-cp-sm-modal__foot">
              <button
                className="justo-cp__btn justo-cp__btn--ghost"
                type="button"
                onClick={closeApproveModal}
              >
                Cancel
              </button>
              <button
                className="justo-cp__btn justo-cp__btn--solid"
                type="button"
                onClick={() => handleApprove(selectedItem)}
                disabled={!selectedSourcingManager}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CpSmList;
